import { Injectable } from '@angular/core';
import { ItemStatus } from '@enums/item-status.enum';
import { DirectusQuery } from '@interfaces/directus-query.interface';
import { DirectusSchema } from '@interfaces/directus-schema.interface';
import { map, Observable } from 'rxjs';

import { DataService } from '../data/data.service';

@Injectable({
  providedIn: 'root'
})
export class CategoriesGroupsService extends DataService {
  protected override collection: keyof DirectusSchema = 'categories_groups';
  protected override fields = [
    'categories,id,pages_1,pages_2,slug,sort,status,title',
    'categories.*',
    'categories.categories_id.*',
    'pages_1.sort',
    'pages_1.pages_id.*',
    'pages_1.pages_id.groups.*',
    'pages_1.pages_id.groups.groups_id.*',
    'pages_2.sort',
    'pages_2.pages_id.*',
    'pages_2.pages_id.groups.*',
    'pages_2.pages_id.groups.groups_id.*'
  ];

  /*
   * getItems is used to override the extendsQuery for readItems in DataService
   */
  public override getItems<CategoryGroup>(): Observable<CategoryGroup[]> {
    return super
      .getItems(
        this.extendQuery({
          filter: {
            status: ItemStatus.Published
          },
          sort: 'sort'
        })
      )
      .pipe(
        map((categoryGroups: any[]) => (Array.isArray(categoryGroups) ? categoryGroups.map(this.filterPages) : []))
      );
  }

  /*
   * getItem is used to override the default query for getItem
   *
   */
  public override getItem<CategoryGroup>(id: string, force?: boolean): Observable<CategoryGroup | null> {
    return super
      .getItem(id, force)
      .pipe(
        map((categoryGroup: any) => (categoryGroup ? this.filterPages(categoryGroup) : categoryGroup))
      ) as Observable<CategoryGroup>;
  }

  /*
   * Method is used to override the default query for readItems, depending on m2m structure for groups
   */
  protected override extendQuery(query: DirectusQuery): DirectusQuery {
    // set fields from current service
    query.fields = this.fields;
    // set groups from auth service, only if extendQueryWithGroups is true
    const groups = this.authService.getPermissionGroups();
    if (groups.length > 0) {
      query.deep = {
        pages_1: this.getDeepPagesFilter(groups),
        pages_2: this.getDeepPagesFilter(groups)
      };
      // TODO check if here only empty fitler is required, seems to be needed to filter on top level
      query.filter['_or'] = [
        {
          _or: [
            {
              pages_1: this.getGroupFilter(groups)
            },
            {
              pages_1: this.getEmptyGroupFilter()
            }
          ]
        },
        {
          _or: [
            {
              pages_2: this.getGroupFilter(groups)
            },
            {
              pages_2: this.getEmptyGroupFilter()
            }
          ]
        }
      ];
    }
    return query;
  }

  private filterPages(categoryGroup: any): any {
    // TODO find a better to query in the first place, so that pages_id object is not returned at all see getDeepFilter
    // workaround because pages are returned like { pages_id: null }
    categoryGroup.pages_1 = categoryGroup.pages_1.filter((page: any) => page.pages_id !== null);
    categoryGroup.pages_2 = categoryGroup.pages_2.filter((page: any) => page.pages_id !== null);
    return categoryGroup;
  }

  private getGroupFilter(groups: string[]): any {
    return {
      pages_id: {
        groups: {
          groups_id: {
            externalId: {
              _in: groups
            }
          }
        }
      }
    };
  }

  private getEmptyGroupFilter(): any {
    return {
      pages_id: {
        groups: {
          groups_id: {
            _empty: true
          }
        }
      }
    };
  }

  private getDeepPagesFilter(groups: string[]): any {
    return {
      pages_id: {
        _filter: {
          status: {
            _eq: ItemStatus.Published
          },
          groups: {
            groups_id: {
              externalId: {
                _in: groups
              }
            }
          }
        },
        _sort: 'sort'
      }
    };
  }
}
